import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  FileText,
  BookOpen,
  FileSearch,
  ChevronRight,
  Settings,
  Users,
  LogOut,
  User,
  FolderKanban,
  MessageSquareText,
  Sparkles,
  History,
  FilePlus2,
  CircleUserRound,
  X,
  Search,
  Globe,
  Mic,
  BarChart3,
  Mail,
  Bot,
  ThumbsDown
} from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { useAssistantContext } from '../contexts/AssistantContext';

const Sidebar = ({ onClose }) => {
  const { currentUser, isAdmin, logout } = useAuth();
  
  // État pour les menus déroulants
  const [expandedMenus, setExpandedMenus] = useState({
    recherche: false,
    agents: false,
    administration: false,
    compte: false
  });
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // Effet pour ouvrir automatiquement le menu correspondant à la page courante
  useEffect(() => {
    if (location.pathname.includes('/biblio-rag') || location.pathname.includes('/websearch') || location.pathname.includes('/document-search')) {
      setExpandedMenus(prev => ({ ...prev, recherche: true }));
    } else if (location.pathname.includes('/email-writer') || location.pathname.includes('/transcription')) {
      setExpandedMenus(prev => ({ ...prev, agents: true }));
    } else if (location.pathname.includes('/bibliotheque') || location.pathname.includes('/assistants-admin') || location.pathname.includes('/users') || location.pathname.includes('/admin/stats') || location.pathname.includes('/admin/feedbacks') || location.pathname === '/assistants') {
      setExpandedMenus(prev => ({ ...prev, administration: true }));
    } else if (location.pathname.includes('/profile') || location.pathname.includes('/my-space')) {
      setExpandedMenus(prev => ({ ...prev, compte: true }));
    }
  }, [location.pathname]);

  // Fonction pour basculer l'état d'un menu
  const toggleMenu = (menuName) => {
    setExpandedMenus(prev => {
      // Fermer tous les menus
      const allClosed = {
        recherche: false,
        agents: false,
        administration: false,
        compte: false
      };
      // Ouvrir uniquement le menu cliqué (ou le fermer s'il était déjà ouvert)
      return {
        ...allClosed,
        [menuName]: !prev[menuName]
      };
    });
  };
  
  // Vérifier si une route est active
  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '/home';
    }
    // Pour éviter les conflits entre /assistants et /assistants-admin
    if (path === '/assistants') {
      return location.pathname === '/assistants';
    }
    return location.pathname.includes(path);
  };

  // Gérer la déconnexion
  const handleLogout = () => {
    logout();
    navigate('/login');
    if (onClose) onClose();
  };

  // Navigation item component - style Arc
  const NavItem = ({ to, icon: Icon, children, isActive: active, onClick, className = "" }) => {
    const handleClick = () => {
      // Fermer tous les menus déroulants
      setExpandedMenus({
        recherche: false,
        agents: false,
        administration: false,
        compte: false
      });

      if (onClick) {
        onClick();
      }

      if (onClose) {
        onClose();
      }
    };

    const content = (
      <div className={`arc-sidebar-nav-item ${active ? 'active' : ''} ${className} group transition-all duration-200 hover:bg-gray-50`} onClick={onClick && !to ? handleClick : undefined}>
        <Icon size={20} className="mr-3 flex-shrink-0 text-gray-500 transition-transform duration-200 group-hover:translate-x-1" />
        <span className="flex-grow truncate transition-transform duration-200 group-hover:translate-x-1">{children}</span>
      </div>
    );

    if (to) {
      return (
        <Link to={to} onClick={handleClick} className="no-underline">
          {content}
        </Link>
      );
    }

    return content;
  };

  // Expandable menu component - style Arc
  const ExpandableMenu = ({ icon: Icon, title, children, isExpanded, onToggle, isActive: active }) => (
    <div className="space-y-0">
      <div 
        className={`arc-sidebar-nav-item ${active ? 'active' : ''} cursor-pointer group transition-all duration-200 hover:bg-gray-50`}
        onClick={onToggle}
      >
        <Icon size={20} className="mr-3 flex-shrink-0 text-gray-500 transition-transform duration-200 group-hover:translate-x-1" />
        <span className="flex-grow truncate transition-transform duration-200 group-hover:translate-x-1">{title}</span>
        <span className={`transition-transform duration-200 text-gray-400 flex items-center ${isExpanded ? 'rotate-90' : ''}`}>
          <ChevronRight size={14} />
        </span>
      </div>
      
      <div className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="arc-sidebar-submenu">
          {children}
        </div>
      </div>
    </div>
  );

  return (
    <div className="arc-sidebar">
      {/* Close button for mobile */}
      <button
        onClick={onClose}
        className="lg:hidden absolute top-4 right-4 z-10 p-2 text-gray-500 hover:text-gray-700 transition-colors"
        aria-label="Fermer le menu"
      >
        <X size={18} />
      </button>

      {/* Logo en haut à gauche */}
      {process.env.REACT_APP_LOGO_FILENAME && (
        <div className="p-4 flex justify-center">
          <img
            src={`/static/logo/${process.env.REACT_APP_LOGO_FILENAME}`}
            alt="Logo"
            className="h-16 w-auto object-contain"
          />
        </div>
      )}

      {/* Separator line après le logo */}
      {process.env.REACT_APP_LOGO_FILENAME && (
        <div className="mx-4 my-0">
          <div className="h-px bg-gray-200"></div>
        </div>
      )}

      {/* Header avec titre - affiché uniquement en mode non connecté */}
      {!currentUser && (
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Sparkles size={20} className="text-white" />
            </div>
            <div>
              <h1 className="font-bold text-gray-600 text-xl tracking-tight">Chat Consult</h1>
              <p className="text-xs text-gray-500 -mt-1">Assistant IA</p>
            </div>
          </div>
        </div>
      )}

      {/* User info section - affiché uniquement en mode connecté */}
      {currentUser && (
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white flex items-center justify-center text-sm font-medium">
              {currentUser.first_name ? currentUser.first_name.charAt(0).toUpperCase() :
                currentUser.username.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate">
                {currentUser.first_name ? `${currentUser.first_name} ${currentUser.last_name || ''}` : currentUser.username}
              </p>
              <p className="text-xs text-gray-500 truncate">{currentUser.email}</p>
            </div>
          </div>
        </div>
      )}

      {/* Separator line après la section utilisateur ou Chat Consult */}
      <div className="mx-4 mt-0 mb-3">
        <div className="h-px bg-gray-200"></div>
      </div>

      {/* Collapsible sidebar container */}
      <div className="collapsible-sidebar" aria-expanded="true">
        
        {/* Sidebar body/content */}
        <div className="collapsible-sidebar-body">
          <nav className="space-y-1">
            {/* Main navigation items */}
            <NavItem to="/" icon={Home} isActive={isActive('/')}>
              Accueil
            </NavItem>

            <NavItem to="/avatars" icon={MessageSquareText} isActive={isActive('/avatars')}>
              Assistants
            </NavItem>

            {/* Menu Recherche avec sous-menus */}
            <ExpandableMenu
              icon={Search}
              title="Recherche"
              isExpanded={expandedMenus.recherche}
              onToggle={() => toggleMenu('recherche')}
            >
              <NavItem to="/biblio-rag" icon={FileSearch} isActive={isActive('/biblio-rag')}>
                Bibliothèque
              </NavItem>
              <NavItem to="/websearch" icon={Globe} isActive={isActive('/websearch')}>
                Recherche internet
              </NavItem>
              <NavItem to="/document-search" icon={FilePlus2} isActive={isActive('/document-search')}>
                Ajouter un document
              </NavItem>
            </ExpandableMenu>

            {/* Menu Agents avec sous-menus */}
            <ExpandableMenu
              icon={Bot}
              title="Agents"
              isExpanded={expandedMenus.agents}
              onToggle={() => toggleMenu('agents')}
            >
              <NavItem to="/email-writer" icon={Mail} isActive={isActive('/email-writer')}>
                Rédacteur d'emails
              </NavItem>
              <NavItem to="/transcription" icon={Mic} isActive={isActive('/transcription')}>
                Transcription audio
              </NavItem>
            </ExpandableMenu>

            <NavItem to="/projects" icon={FolderKanban} isActive={isActive('/projects')}>
              Projets
            </NavItem>

            {/* Mon espace submenu */}
            <ExpandableMenu
              icon={CircleUserRound}
              title="Mon espace"
              isExpanded={expandedMenus.compte}
              onToggle={() => toggleMenu('compte')}
            >
              <NavItem to="/my-space" icon={History} isActive={isActive('/my-space')}>
                Historique
              </NavItem>
              <NavItem to="/profile" icon={User} isActive={isActive('/profile')}>
                Mon profil
              </NavItem>

              <NavItem icon={LogOut} onClick={handleLogout} >
                Déconnexion
              </NavItem>
            </ExpandableMenu>

            {/* Menu Administration - visible uniquement pour les admins */}
            {isAdmin && (
              <ExpandableMenu
                icon={Settings}
                title="Administration"
                isExpanded={expandedMenus.administration}
                onToggle={() => toggleMenu('administration')}
              >
                <NavItem to="/users" icon={Users} isActive={isActive('/users')}>
                  Gestion utilisateurs
                </NavItem>
                <NavItem to="/assistants-admin" icon={Settings} isActive={isActive('/assistants-admin')}>
                  Gestion Assistants
                </NavItem>                
                <NavItem to="/assistants" icon={MessageSquareText} isActive={isActive('/assistants')}>
                  Catalogue Assistants
                </NavItem>
                <NavItem to="/bibliotheque" icon={BookOpen} isActive={isActive('/bibliotheque')}>
                  Base documentaire
                </NavItem>
                <NavItem to="/admin/stats" icon={BarChart3} isActive={isActive('/admin/stats')}>
                  Statistiques
                </NavItem>
                <NavItem to="/admin/feedbacks" icon={ThumbsDown} isActive={isActive('/admin/feedbacks')}>
                  Feedbacks
                </NavItem>
              </ExpandableMenu>
            )}
            
            {/* Documentation */}
            <NavItem to="/aide" icon={FileText} isActive={isActive('/aide')}>
              Aide
            </NavItem>
          </nav>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-auto p-4 border-t border-gray-100">
        <div className="text-center">
          <p className="text-xs text-gray-500">
            © 2026 ChatConsult -{' '}
            <Link to="/cgu" className="text-gray-500 hover:text-gray-700 transition-colors no-underline">
              CGU
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;